import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { money, CONDITION_LABELS } from '../../constants/marketplace';
import { saveListing, unsaveListing } from '../../services/marketplace';

// ─── Inline stroked SVG icons (Lucide / Feather style — never emojis) ───────
const ImageIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
);

const PinIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const BookmarkIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

const SavedCountIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

// A marketplace listing card for the responsive grid. Mirrors the app's
// MarketplaceListingCard: thumbnail, price, title, condition badge, location,
// saved count. Wraps the whole card in a Link to the detail page; the optional
// bookmark toggle is a button layered on top of the media.
export default function MarketplaceListingCard({ listing, onSave }) {
  const thumb =
    listing.primaryThumbnailUrl ||
    listing.primaryImageUrl ||
    listing.images?.[0]?.thumbnailUrl ||
    listing.images?.[0]?.imageUrl ||
    null;

  const loc = [listing.suburb, listing.region].filter(Boolean).join(', ');
  const sold = listing.status === 'sold';
  const savedCount = Number(listing.savedCount || 0);

  const [saved, setSaved] = useState(!!listing.isSaved);
  const [busy, setBusy] = useState(false);

  const toggleSave = async (e) => {
    // Don't let the bookmark click navigate via the wrapping Link.
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    const next = !saved;
    setSaved(next);
    setBusy(true);
    try {
      if (next) await saveListing(listing.id);
      else await unsaveListing(listing.id);
      if (onSave) onSave(listing.id, next);
    } catch {
      setSaved(!next); // revert on failure
    } finally {
      setBusy(false);
    }
  };

  return (
    <Link to={`/marketplace/${listing.id}`} className="mp-card">
      <div className="mp-card__media">
        {thumb ? (
          <img className="mp-card__img" src={thumb} alt={listing.title || 'Listing'} loading="lazy" />
        ) : (
          <div className="mp-card__img-placeholder"><ImageIcon /></div>
        )}

        {sold && <div className="mp-card__sold">SOLD</div>}

        {listing.condition ? (
          <div className="mp-card__cond">
            {CONDITION_LABELS[listing.condition] || listing.condition}
          </div>
        ) : null}

        {onSave !== undefined ? (
          <button
            type="button"
            className={`mp-card__save${saved ? ' is-saved' : ''}`}
            onClick={toggleSave}
            aria-label={saved ? 'Remove from saved' : 'Save listing'}
            title={saved ? 'Saved' : 'Save'}
          >
            <BookmarkIcon size={16} />
          </button>
        ) : null}
      </div>

      <div className="mp-card__body">
        <div className="mp-card__price">{money(listing.price, listing.currency)}</div>
        <div className="mp-card__title">{listing.title}</div>

        {listing.wheelBoltPattern ? (
          <div className="mp-card__spec">
            {listing.wheelBoltPattern}
            {listing.wheelDiameterIn ? `  ${Number(listing.wheelDiameterIn)}"` : ''}
            {listing.wheelWidthIn ? `x${Number(listing.wheelWidthIn)}` : ''}
          </div>
        ) : null}

        {(loc || savedCount > 0) ? (
          <div className="mp-card__meta">
            {loc ? (
              <>
                <PinIcon size={11} />
                <span>{loc}</span>
              </>
            ) : null}
            {savedCount > 0 ? (
              <span className="mp-card__saved-count">
                <SavedCountIcon size={11} />
                {savedCount}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </Link>
  );
}
